import StatusIndicator from "../utils/StatusIndicator";

export type imageValidationsProps = {
    imageSize: number | null;
    imageContentType: string | null;
    imageAspectRatio: number | null;
}

export const errorForFileSize  = (imageSize: number | null) : string => {
    if (!imageSize || imageSize <= 0) {
        return 'File size is required but appears to be 0.';
    }
    if (imageSize > 800) {
        return `Max file size is 800KB but this image is ${imageSize}KB.`;
    }
    return 'OK'
}

export const isValidAspectRatio = (aspectRatio: number | null) : boolean => {
    if (!aspectRatio) {
        return false;
    }
    return aspectRatio >= 0.5 && aspectRatio <= 2;
}

export const errorForAspectRatio = (aspectRatio: number | null) : string => {
    if (!aspectRatio) {
        return 'Aspect ratio is required but appears to be null.';
    }
    const displayAspectRatio = aspectRatio.toFixed(2)

    if (aspectRatio < 0.5) {
        return `Aspect ratio must be at least 0.5 but this image is ${displayAspectRatio}. It is too thin.`;
    }
    if (aspectRatio > 2) {
        return `Aspect ratio must be at most 2 but this image is ${displayAspectRatio}. It is too wide.`;
    }
    return 'OK'
}

export const isValidContentType = (contentType: string | null) : boolean => {
    const allowedContentTypes = ['image/jpeg', 'image/png'];
    if (!contentType) {
        return false;
    }
    return  allowedContentTypes.includes(contentType)
}

export const contentTypeFileExtension = (contentType: string | null) : string | null => {
    if (!contentType) {
        return null;
    }
    if (contentType === 'image/jpeg') {
        return 'jpg';
    }
    if (contentType === 'image/png') {
        return 'png';
    }
    throw new Error(`Unknown content type: ${contentType}`);
}

const errorForContentType = (contentType: string | null) : string => {
    if (!contentType) {
        return 'Content type is required but appears to be null.';
    }
    if (!isValidContentType(contentType)) {
        return `Content type must be jpeg or png but this image is ${contentType}.`;
    }
    return 'OK'
}

const ImageUploadPreview = (props: imageValidationsProps) => {
    const { imageSize, imageContentType, imageAspectRatio } = props;

    const fileSizeIsValid = imageSize && imageSize <= 800;
    const contentTypeIsValid = isValidContentType(imageContentType);
    const aspectRatioIsValid = imageAspectRatio && imageAspectRatio >= 0.5 && imageAspectRatio <= 2;
    const checks = [fileSizeIsValid, contentTypeIsValid, aspectRatioIsValid];
    const imageIsFullyValid = checks.every(element => element === true);
    const summaryTextcolor = imageIsFullyValid ? 'text-green-800' : 'text-red-800';

    const summaryDescription = imageIsFullyValid ? 'Valid Image File' : 'This image file cannot be used in the game.';

    return (
        <div className='mt-4 flex flex-col gap-y-5 text-center items-center justify-center'>
            <h3 className={summaryTextcolor}>
            <StatusIndicator isSuccess={imageIsFullyValid} /> {summaryDescription}
            </h3>
            {!imageIsFullyValid && (
                <div className='w-80 text-center'>
                    <div className='mt-2'>
                        <StatusIndicator isSuccess={fileSizeIsValid} />
                        <span className='p-2'>File size validation: </span>
                        {errorForFileSize(imageSize)}
                    </div>
                    <div className='mt-2'>
                        <StatusIndicator isSuccess={contentTypeIsValid}/>
                        <span className='p-2'>Content type validation: </span>
                        {errorForContentType(imageContentType)}
                    </div>
                    <div className='mt-2'>
                        <StatusIndicator isSuccess={aspectRatioIsValid} />
                        <span className='p-2'>Aspect ratio validation:</span>
                        {errorForAspectRatio(imageAspectRatio)}
                    </div>
                </div>
            )}
        </div>
        );
};

export default ImageUploadPreview;
